import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO, { breadcrumbJsonLd, collectionPageJsonLd } from '../components/SEO';
import { dalcommGseAccessories } from '../data/dalcomm';

/**
 * /products/dalcomm/gse-adapters — hub page listing every Dalcomm Tech
 * GSE headset, adapter, and accessory.  Each card links out to the
 * individual /products/dalcomm/gse-adapters/:slug detail page.
 *
 * The Wireless Intercom Kit is intentionally NOT included here — it has
 * its own top-level product page at /products/dalcomm/wireless-intercom-kit.
 */
const DalcommGSEAdaptersHubPage = ({ onRequestDemo, onRequestQuote }) => {
  const canonicalUrl = 'https://ctrl.aero/products/dalcomm/gse-adapters';

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Dalcomm GSE Headsets, Adapters & Accessories | ctrl.aero"
        description="Ground-support equipment headsets, wireless intercom accessories, PTT and extension cables, and ramp accessories from Dalcomm Tech — carried by ctrl.aero."
        canonical={canonicalUrl}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://ctrl.aero/' },
            { name: 'Products', url: 'https://ctrl.aero/#solutions' },
            { name: 'Dalcomm GSE Adapters & Accessories', url: canonicalUrl },
          ]),
          collectionPageJsonLd({
            name: 'Dalcomm GSE Headsets, Adapters & Accessories',
            description:
              'Full catalog of Dalcomm Tech ground-support-equipment headsets, wireless intercom accessories, and ramp adapters carried by ctrl.aero.',
            url: canonicalUrl,
            items: dalcommGseAccessories.map((p) => ({
              type: 'Product',
              name: p.name,
              url: `https://ctrl.aero/products/dalcomm/gse-adapters/${p.slug}`,
            })),
          }),
        ]}
      />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1e2d3b] via-[#243447] to-[#1e2d3b] pt-24 pb-14 sm:pt-28 sm:pb-20 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-blue-500/20 blur-3xl"
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link
            to="/"
            className="inline-flex items-center text-white/70 hover:text-white text-sm mb-6 transition-colors"
            data-testid="dalcomm-hub-back-home"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
          </Link>

          <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 border border-blue-400/30 text-blue-300 text-xs sm:text-sm font-semibold tracking-wide uppercase mb-6">
            Dalcomm Tech · GSE &amp; Ramp
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
            GSE Headsets, Adapters &amp; Accessories
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-blue-100/85 leading-relaxed">
            Every piece of Dalcomm ground-crew gear we carry — wireless headsets and gateway
            components, hardwired ramp headsets, safety-orange PTT and extension cables,
            aircraft interphone adapters, and the small ramp consumables that keep it all
            running.
          </p>

          <div className="mt-6">
            <Link
              to="/products/dalcomm/wireless-intercom-kit"
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-300 hover:text-white transition-colors group"
              data-testid="dalcomm-hub-intercom-kit-link"
            >
              Looking for the full Wireless Intercom Kit?
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Product grid */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {dalcommGseAccessories.map((p) => (
              <Link
                key={p.slug}
                to={`/products/dalcomm/gse-adapters/${p.slug}`}
                className="group relative flex flex-col overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-300"
                data-testid={`dalcomm-accessory-card-${p.slug}`}
              >
                <div className="relative aspect-square overflow-hidden bg-white flex items-center justify-center p-6">
                  {p.image && (
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="flex-1 flex flex-col p-5 border-t border-slate-100">
                  <div className="text-xs font-semibold tracking-wider uppercase text-blue-600 mb-2">
                    {p.price}
                  </div>
                  <h2 className="text-lg font-bold text-[#1e2d3b] tracking-tight group-hover:text-blue-600 transition-colors leading-snug">
                    {p.name}
                  </h2>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed line-clamp-3 flex-1">
                    {p.tagline}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 group-hover:text-blue-700">
                    View details
                    <ArrowRight
                      className="w-4 h-4 transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1e2d3b] tracking-tight">
            Building out a pushback or ramp crew?
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Talk to us about a bundled quote across headsets, adapters, and consumables.
            We ship globally to airlines, MRO operators, and GSE fleets.
          </p>
          <div className="mt-8 cta-group max-w-lg mx-auto">
            <Button
              onClick={onRequestDemo}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-[15px] font-semibold"
              data-testid="dalcomm-hub-request-demo"
            >
              Request Demo
            </Button>
            <Button
              onClick={onRequestQuote}
              className="bg-orange-500 hover:bg-orange-600 text-white text-sm sm:text-[15px] font-semibold"
              data-testid="dalcomm-hub-request-quote"
            >
              Request Quote
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DalcommGSEAdaptersHubPage;
